import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useRoutes, Navigate } from 'react-router-dom';
import ROUTE_PATH from '../config/routes.config';
import LoginView from '../views/auth/login.view';
import DashboardView from '../views/dashboard/dashboard.view';
import RealtimeDashboardView from '../views/dashboard/realtime-dashboard.view';
import ContentVideosView from '../views/content/videos.view';
import ContentSequenceView from '../views/content/content-sequence.view';
import SubscribersView from '../views/subscribers/subscribers.view';
import InvitedSubscribersView from '../views/subscribers/invited-subscribers.view';
import ReportsOverviewView from '../views/reports/overview.view';
import ReportsLiveUsersView from '../views/reports/live-users.view';
import ReportsSessionsView from '../views/reports/sessions.view';
import PageNotFoundView from '../views/errors/not-found.view';
import LogoutView from '../views/auth/logout.view';
import NotificationsView from '../views/settings/notification.view';
import SalutationView from '../views/settings/salutation.view';
import ContentCategoriesView from '../views/content/categories.view';
import ContentUserTestimonialTextView from '../views/content/user-testimonial-text.view';
import ContentUserTestimonialVideoView from '../views/content/user-testimonial-video.view';
import RecordedMessageView from '../views/content/recorded-message.view';
import DataChangeLogsView from '../views/reports/data-change-logs.view';
import {
  SHOW_CONTENT_CATEGORIES,
  SHOW_CONTENT_SEQUENCE,
  SHOW_CONTENT_VIDEOS,
  SHOW_NOTIFICATIONS,
  SHOW_OVERVIEW_DASHBOARD,
  SHOW_REALTIME_DASHBOARD,
  SHOW_REPORTS_LIVE_USERS,
  SHOW_REPORTS_OVERVIEW_VIDEOS,
  SHOW_REPORTS_WATCH_SESSIONS,
  SHOW_SALUTATION,
  SHOW_SUBSCRIBERS,
  SHOW_TESTIMONIALS,
  SHOW_GROUPS,
  SHOW_CONTENT_GATHERINGS,
  SHOW_REPORTS_LIVE_EVENT,
  SHOW_CONTENT_RESOURCES,
  SHOW_RECORD_MESSAGE,
  SHOW_LEADING_TEACHERS,
  CONTENT_ALIAS,
  SHOW_ADMIN_USERS,
  SHOW_QUIZ,
  SHOW_DATA_CHANGE_LOG,
} from '../config/const.config';
import GroupsView from '../views/groups/groups.view';
import AdminUsersView from '../views/admin-users/admin-users.view';
import ContentCommunityGatheringView from '../views/content/community-gathering.view';
import ManageMembersView from '../views/groups/manage-members.view';
import EventRegistrationsView from '../views/content/event-registrations.view';
import ManageRecuringEventView from '../views/content/manage-recuring-event.view';
import ReportsLiveEventView from '../views/reports/live-event.view';
import ResourcesContentView from '../views/content/resources-content.view';
import LeadingTeachersView from '../views/content/leading-teachers.view';
import QuizView from '../views/quiz/quiz.view';
import QuizQuestionsView from '../views/quiz/quiz-questions.view';
import QuizResultsView from '../views/quiz/quiz-results.view';
import QuizSubscriberResultsView from '../views/quiz/quiz-subscriber-results.view';
import useAccess from '../hooks/access.hook';
import { AccessContext } from '../context/access.context';

const AppRouter = ({ isLoggedIn }) => {
  const { accessData } = useContext(AccessContext);
  const { hasOperationAccess } = useAccess();
  const routeMappings = [];

  if (isLoggedIn) {
    routeMappings.push({
      path: ROUTE_PATH.LOGIN,
      element: <Navigate to={ROUTE_PATH.DASHBOARD} />,
      exact: true,
    });

    if (
      SHOW_OVERVIEW_DASHBOARD &&
      hasOperationAccess(accessData.role, 'dashboard', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.DASHBOARD,
        element: <DashboardView />,
        exact: true,
      });
    }

    if (
      SHOW_REALTIME_DASHBOARD &&
      hasOperationAccess(accessData.role, 'dashboard', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.REALTIME_DASHBOARD,
        element: <RealtimeDashboardView />,
        exact: true,
      });
    }

    if (
      SHOW_SUBSCRIBERS &&
      hasOperationAccess(accessData.role, 'subscribers', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.SUBSCRIBERS,
        element: <SubscribersView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.INVITED_SUBSCRIBERS,
        element: <InvitedSubscribersView />,
        exact: true,
      });
    }

    if (
      SHOW_GROUPS &&
      hasOperationAccess(accessData.role, 'community_groups', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.GROUPS,
        element: <GroupsView />,
        exact: true,
      });

      routeMappings.push({
        path: ROUTE_PATH.GROUPS_MANAGE_MEMBERS,
        element: <ManageMembersView />,
        exact: true,
      });
    }

    if (SHOW_QUIZ && hasOperationAccess(accessData.role, 'quiz', 'view')) {
      routeMappings.push({
        path: ROUTE_PATH.QUIZ,
        element: <QuizView />,
        exact: true,
      });

      if (hasOperationAccess(accessData.role, 'quiz', 'manage_questions')) {
        routeMappings.push({
          path: ROUTE_PATH.QUIZ_MANAGE_QUESTIONS,
          element: <QuizQuestionsView />,
          exact: true,
        });
      }

      if (hasOperationAccess(accessData.role, 'quiz', 'view_results')) {
        routeMappings.push({
          path: ROUTE_PATH.QUIZ_RESULTS,
          element: <QuizResultsView />,
          exact: true,
        });
      }

      if (
        hasOperationAccess(accessData.role, 'quiz', 'view_subscriber_results')
      ) {
        routeMappings.push({
          path: ROUTE_PATH.QUIZ_SUBSCRIBER_RESULTS,
          element: <QuizSubscriberResultsView />,
          exact: true,
        });
      }
    }

    if (
      SHOW_ADMIN_USERS &&
      hasOperationAccess(accessData.role, 'users', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.ADMIN_USERS,
        element: <AdminUsersView />,
        exact: true,
      });
    }

    if (
      SHOW_CONTENT_CATEGORIES &&
      hasOperationAccess(accessData.role, 'program_courses', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_CATEGORIES,
        element: <ContentCategoriesView />,
        exact: true,
      });
    }

    if (
      SHOW_CONTENT_VIDEOS &&
      hasOperationAccess(accessData.role, 'videos', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_VIDEOS,
        element: <ContentVideosView />,
        exact: true,
      });

      if (hasOperationAccess(accessData.role, 'videos', 'view_registrations')) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_VIDEOS_EVENT_REGISTRATIONS,
          element: <EventRegistrationsView />,
          exact: true,
        });
      }
      if (
        hasOperationAccess(accessData.role, 'videos', 'manage_recurring_events')
      ) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_MANAGE_RECURRING_EVENTS,
          element: <ManageRecuringEventView />,
          exact: true,
        });
      }
    }

    if (
      SHOW_CONTENT_RESOURCES &&
      hasOperationAccess(accessData.role, 'resources', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_RESOURCES,
        element: <ResourcesContentView />,
        exact: true,
      });
    }

    if (
      SHOW_CONTENT_GATHERINGS &&
      hasOperationAccess(accessData.role, 'community_gatherings_zoom', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_GATHERINGS,
        element: <ContentCommunityGatheringView />,
        exact: true,
      });
    }

    if (
      SHOW_LEADING_TEACHERS &&
      hasOperationAccess(accessData.role, 'leading_teachers', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_TEACHERS,
        element: <LeadingTeachersView />,
        exact: true,
      });
    }

    if (SHOW_RECORD_MESSAGE) {
      if (
        hasOperationAccess(accessData.role, 'ms_voice_recorded_videos', 'view')
      ) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_RECORDED_VIDEOS,
          element: <RecordedMessageView type="VIDEO" />,
          exact: true,
        });
      }

      if (
        hasOperationAccess(accessData.role, 'ms_voice_recorded_audios', 'view')
      ) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_RECORDED_AUDIOS,
          element: <RecordedMessageView type="AUDIO" />,
          exact: true,
        });
      }
    }

    if (
      SHOW_CONTENT_SEQUENCE &&
      hasOperationAccess(accessData.role, 'sequence', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.CONTENT_SEQUENCE_CATEGORY,
        element: <ContentSequenceView type={CONTENT_ALIAS.CATEGORY_SINGULAR} />,
        exact: true,
      });
    }

    if (SHOW_TESTIMONIALS) {
      if (
        hasOperationAccess(
          accessData.role,
          'testimonials_user_text_testimonials',
          'view'
        )
      ) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_USER_TESTIMONIALS_TEXT,
          element: <ContentUserTestimonialTextView />,
          exact: true,
        });
      }

      if (
        hasOperationAccess(
          accessData.role,
          'testimonials_user_video_testimonials',
          'view'
        )
      ) {
        routeMappings.push({
          path: ROUTE_PATH.CONTENT_USER_TESTIMONIALS_VIDEO,
          element: <ContentUserTestimonialVideoView />,
          exact: true,
        });
      }
    }

    if (
      SHOW_NOTIFICATIONS &&
      hasOperationAccess(accessData.role, 'notifications', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.NOTIFICATIONS,
        element: <NotificationsView />,
        exact: true,
      });
    }

    if (
      SHOW_SALUTATION &&
      hasOperationAccess(accessData.role, 'salutation', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.SALUTATION,
        element: <SalutationView />,
        exact: true,
      });
    }

    if (
      SHOW_REPORTS_OVERVIEW_VIDEOS &&
      hasOperationAccess(accessData.role, 'video_watch_overview', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_OVERVIEW_VIDEOS,
        element: <ReportsOverviewView />,
        exact: true,
      });
    }

    if (
      SHOW_REPORTS_LIVE_USERS &&
      hasOperationAccess(accessData.role, 'live_users', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_LIVE_USERS,
        element: <ReportsLiveUsersView />,
        exact: true,
      });
    }

    if (
      SHOW_REPORTS_WATCH_SESSIONS &&
      hasOperationAccess(accessData.role, 'video_watch_sessions', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_SESSIONS,
        element: <ReportsSessionsView />,
        exact: true,
      });
    }

    if (
      SHOW_REPORTS_LIVE_EVENT &&
      hasOperationAccess(accessData.role, 'live_event_report', 'view')
    ) {
      routeMappings.push({
        path: ROUTE_PATH.REPORTS_LIVE_EVENT,
        element: <ReportsLiveEventView />,
        exact: true,
      });
    }

    if (SHOW_DATA_CHANGE_LOG) {
      routeMappings.push({
        path: ROUTE_PATH.DATA_CHANGE_LOGS,
        element: <DataChangeLogsView />,
        exact: true,
      });
    }

    routeMappings.push({
      path: ROUTE_PATH.LOGOUT,
      element: <LogoutView />,
      exact: true,
    });
  }

  routeMappings.push({
    path: '*',
    element: isLoggedIn ? <PageNotFoundView /> : <LoginView />,
  });

  const routes = useRoutes(routeMappings);

  return routes;
};

AppRouter.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  accessRole: PropTypes.string.isRequired,
};

export default AppRouter;
